// src/lib/sentry.js
// Sentry error monitoring and performance tracing configuration
// Initializes Sentry with privacy-preserving settings

import * as Sentry from '@sentry/react';

const isDev = import.meta.env.DEV;
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || (isDev ? 'development' : 'production');
const TRACES_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 0.1;

// Patterns to detect and sanitize sensitive data (matching logger.js)
const SENSITIVE_PATTERNS = [
  { pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, replacement: '[EMAIL]' },
  { pattern: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, replacement: '[ID]' },
  { pattern: /eyJ[a-zA-Z0-9_-]*\.eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g, replacement: '[TOKEN]' },
  { pattern: /Bearer\s+[a-zA-Z0-9._-]+/gi, replacement: '[TOKEN]' },
  { pattern: /sk_[a-zA-Z0-9]{20,}/g, replacement: '[KEY]' },
  { pattern: /pk_[a-zA-Z0-9]{20,}/g, replacement: '[KEY]' },
  { pattern: /sbp_[a-zA-Z0-9]{20,}/g, replacement: '[KEY]' },
  { pattern: /re_[a-zA-Z0-9_]{20,}/g, replacement: '[KEY]' }, // Resend API keys
];

// Sensitive keys to redact entirely
const SENSITIVE_KEYS = [
  'password', 'token', 'secret', 'key', 'authorization',
  'credential', 'apikey', 'api_key', 'accesstoken', 'refreshtoken'
];

/**
 * Sanitize a string by replacing sensitive data patterns
 */
const sanitizeString = (str) => {
  if (typeof str !== 'string') return str;
  let sanitized = str;
  SENSITIVE_PATTERNS.forEach(({ pattern, replacement }) => {
    sanitized = sanitized.replace(pattern, replacement);
  });
  return sanitized;
};

/**
 * Deep sanitize an object for Sentry
 */
const sanitizeData = (data, depth = 0) => {
  if (depth > 10) return '[MAX_DEPTH]';
  if (data === null || data === undefined) return data;

  if (typeof data === 'string') {
    return sanitizeString(data);
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item, depth + 1));
  }

  if (typeof data === 'object') {
    const sanitized = {};
    for (const key of Object.keys(data)) {
      const lowerKey = key.toLowerCase();
      if (SENSITIVE_KEYS.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else if (lowerKey === 'email' || lowerKey.includes('email')) {
        sanitized[key] = '[EMAIL]';
      } else {
        sanitized[key] = sanitizeData(data[key], depth + 1);
      }
    }
    return sanitized;
  }

  return data;
};

/**
 * Errors to ignore (common browser noise)
 */
const IGNORED_ERRORS = [
  // Browser extension errors
  /^Script error\.?$/i,
  /^ResizeObserver loop/i,
  // Network errors that are expected
  /^NetworkError/i,
  /^AbortError/i,
  /^Load failed$/i,
  // Auth-related that we handle gracefully
  /invalid refresh token/i,
  /JWT expired/i,
];

/**
 * Initialize Sentry with privacy-preserving configuration
 */
export const initSentry = () => {
  // Skip if no DSN configured
  if (!SENTRY_DSN) {
    if (isDev) {
      console.log('[Sentry] No DSN configured, skipping initialization');
    }
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,

      // Performance monitoring - 10% sample rate for free tier
      tracesSampleRate: TRACES_SAMPLE_RATE,

      // Only send errors in production (or all in dev for testing)
      enabled: !isDev || import.meta.env.VITE_SENTRY_DEBUG === 'true',

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Mask all text and inputs for privacy
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],

      // Session replay sample rates (conservative for free tier)
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.1,

      // Privacy: sanitize data before sending
      beforeSend(event, hint) {
        // Check if error should be ignored
        const errorMessage = event.exception?.values?.[0]?.value || '';
        if (IGNORED_ERRORS.some(pattern => pattern.test(errorMessage))) {
          return null;
        }

        // Sanitize exception messages
        if (event.exception?.values) {
          event.exception.values = event.exception.values.map(ex => ({
            ...ex,
            value: sanitizeString(ex.value),
          }));
        }

        // Sanitize breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
            ...breadcrumb,
            message: sanitizeString(breadcrumb.message),
            data: breadcrumb.data ? sanitizeData(breadcrumb.data) : undefined,
          }));
        }

        // Sanitize extra data
        if (event.extra) {
          event.extra = sanitizeData(event.extra);
        }

        // Sanitize user data (keep id for issue grouping, redact PII)
        if (event.user) {
          event.user = {
            id: event.user.id ? sanitizeString(event.user.id) : undefined,
            // Don't send email, username, or IP
          };
        }

        // Sanitize request data
        if (event.request) {
          event.request = {
            ...event.request,
            headers: event.request.headers ? sanitizeData(event.request.headers) : undefined,
            data: event.request.data ? sanitizeData(event.request.data) : undefined,
          };
        }

        return event;
      },

      // Performance: filter transactions
      beforeSendTransaction(event) {
        // Don't send transactions for static assets
        const transactionName = event.transaction || '';
        if (transactionName.includes('/assets/') || transactionName.includes('.js') || transactionName.includes('.css')) {
          return null;
        }
        return event;
      },
    });

    if (isDev) {
      console.log('[Sentry] Initialized successfully');
    }
  } catch (error) {
    // Fail silently - app should work without Sentry
    console.error('[Sentry] Failed to initialize:', error);
  }
};

/**
 * Set user context for error tracking (privacy-safe)
 */
export const setSentryUser = (user) => {
  if (!SENTRY_DSN) return;

  try {
    if (user) {
      Sentry.setUser({
        id: user.id, // Keep ID for grouping, will be sanitized by beforeSend
        // Don't set email, username, or other PII
      });
    } else {
      Sentry.setUser(null);
    }
  } catch (error) {
    // Fail silently
  }
};

/**
 * Add breadcrumb for debugging context
 */
export const addBreadcrumb = (category, message, data = {}, level = 'info') => {
  if (!SENTRY_DSN) return;

  try {
    Sentry.addBreadcrumb({
      category,
      message,
      data: sanitizeData(data),
      level,
    });
  } catch (error) {
    // Fail silently
  }
};

/**
 * Capture an exception manually
 */
export const captureException = (error, context = {}) => {
  if (!SENTRY_DSN) return;

  try {
    Sentry.captureException(error, {
      extra: sanitizeData(context),
    });
  } catch (err) {
    // Fail silently
  }
};

/**
 * Capture a message manually
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  if (!SENTRY_DSN) return;

  try {
    Sentry.captureMessage(sanitizeString(message), {
      level,
      extra: sanitizeData(context),
    });
  } catch (error) {
    // Fail silently
  }
};

/**
 * Set a tag for filtering in Sentry
 */
export const setTag = (key, value) => {
  if (!SENTRY_DSN) return;

  try {
    Sentry.setTag(key, sanitizeString(value));
  } catch (error) {
    // Fail silently
  }
};

// Export Sentry for direct access if needed
export { Sentry };

/**
 * Test function to verify Sentry is working
 * Call from browser console: window.testSentry()
 */
export const testSentry = () => {
  const testError = new Error('Sentry test error - please ignore');
  console.log('[Sentry] Sending test error...');
  captureException(testError, { test: true, timestamp: new Date().toISOString() });
  console.log('[Sentry] Test error sent! Check Sentry dashboard.');
  return 'Test error sent to Sentry';
};

// Expose test function globally for easy console access
if (typeof window !== 'undefined') {
  window.testSentry = testSentry;
}
